import Header from "@Front/Components/DesignSystem/Header";
import Version from "@Front/Components/DesignSystem/Version";
import BackArrow from "@Front/Components/Elements/BackArrow";
import { ReactNode } from "react";
import { useLogLayoutContextSource } from "@Front/Hooks/useLogLayoutContextSource";

import classes from "./classes.module.scss";
import SearchBlockList, { ISearchBlockListProps } from "@Front/Components/DesignSystem/SearchBlockList";
import Footer from "@Front/Components/DesignSystem/Footer";

export { SIDEBAR_LIST_COMPACT_SEARCH } from "./dashboardListSidebarDefaults";

export type IPropsDashboardWithList = {
	title?: string;
	children?: ReactNode;
	hasBackArrow?: boolean;
	backArrowUrl?: string;
	mobileBackText?: string;
	headerConnected?: boolean;
	noPadding?: boolean;
	blocksKey?: string;
	/** Renders above the folder search/list in the left sidebar (e.g. office selector for notary). */
	sidebarTop?: ReactNode;
	/** Renders above the search bar in the sidebar (e.g. "Dossiers de l'office" / "Dossiers invités" context buttons). */
	sidebarAboveSearch?: ReactNode;
	/** Renders between search bar and block list in the sidebar. */
	sidebarBetweenSearchAndList?: ReactNode;
	/** Réduit la hauteur du champ « Chercher » dans la colonne liste ; utiliser **`SIDEBAR_LIST_COMPACT_SEARCH`** pour les listes entités. */
	sidebarCompactSearch?: boolean;
};

type IProps = IPropsDashboardWithList & ISearchBlockListProps;

function DashboardSidebar(props: {
	sidebarTop?: ReactNode;
	blocksKey?: string;
	blocks: ISearchBlockListProps["blocks"];
	onSelectedBlock: ISearchBlockListProps["onSelectedBlock"];
	bottomButton?: ISearchBlockListProps["bottomButton"];
	contentAboveSearch?: ISearchBlockListProps["contentAboveSearch"];
	contentBetweenSearchAndBlocks?: ISearchBlockListProps["contentBetweenSearchAndBlocks"];
	compactSearchBar?: boolean;
	onServerSearch?: ISearchBlockListProps["onServerSearch"];
}): React.JSX.Element {
	const { sidebarTop, blocksKey, blocks, onSelectedBlock, bottomButton, contentAboveSearch, contentBetweenSearchAndBlocks, compactSearchBar, onServerSearch } = props;
	return (
		<>
			{sidebarTop}
			<SearchBlockList
				key={blocksKey}
				blocks={blocks}
				onSelectedBlock={onSelectedBlock}
				bottomButton={bottomButton}
				contentAboveSearch={contentAboveSearch}
				contentBetweenSearchAndBlocks={contentBetweenSearchAndBlocks}
				compactSearchBar={compactSearchBar}
				onServerSearch={onServerSearch}
			/>
		</>
	);
}

function DashboardRightSide(props: { hasBackArrow?: boolean; backArrowUrl?: string; noPadding?: boolean; children?: ReactNode }): React.JSX.Element {
	useLogLayoutContextSource("DefaultDashboardWithList");
	const { hasBackArrow, backArrowUrl, noPadding = false, children } = props;
	return (
		<div className={classes["right-side"]} data-no-padding={noPadding}>
			<div className={classes["right-side-content"]}>
				{hasBackArrow && (
					<div className={classes["back-arrow-desktop"]}>
						<BackArrow url={backArrowUrl ?? ""} />
					</div>
				)}
				{children}
			</div>
			<Footer />
		</div>
	);
}

export default function DefaultDashboardWithList(props: IProps): React.JSX.Element {
	const {
		hasBackArrow,
		backArrowUrl,
		children,
		blocks,
		onSelectedBlock,
		headerConnected = true,
		bottomButton,
		noPadding = false,
		sidebarTop,
		sidebarAboveSearch,
		sidebarBetweenSearchAndList,
		sidebarCompactSearch,
		blocksKey,
		onServerSearch,
	} = props;

	return (
		<div className={classes["root"]}>
			<Header isUserConnected={headerConnected} />
			<div className={classes["content"]}>
				<div className={classes["sidebar-column"]} data-has-top={Boolean(sidebarTop)}>
					<DashboardSidebar
						sidebarTop={sidebarTop}
						blocksKey={blocksKey}
						blocks={blocks}
						onSelectedBlock={onSelectedBlock}
						bottomButton={bottomButton}
						contentAboveSearch={sidebarAboveSearch}
						contentBetweenSearchAndBlocks={sidebarBetweenSearchAndList}
						compactSearchBar={sidebarCompactSearch}
						onServerSearch={onServerSearch}
					/>
				</div>
				<DashboardRightSide hasBackArrow={hasBackArrow} backArrowUrl={backArrowUrl} noPadding={noPadding}>{children}</DashboardRightSide>
			</div>
			<Version />
		</div>
	);
}
